import { getChromeIdle } from '../../chrome/wrappers';
import {
  now,
  getMsFromSeconds,
  isSameDay,
  getTimeStringFromDuration,
  getDateTime,
  getISOfromDateTime,
  getStartOfDay,
  getDateMs,
  getDurationFromMs,
  getSystemTimezone,
  trustedTime,
  toSeconds,
} from '../../lib/dateTime';
import {
  getRulesStore,
  getDeviceAndUserStore,
  getReportingStore,
  getScreenTimeStore,
} from '../../storage/stores';
import { ScreenTime } from '../../types/store/screenTime';
import {
  isSlotCompletedWhileScreenOff,
  isSlotCompletedWhileScreenOn,
  onSlotCompleted,
  onSlotCompletedWhileScreenOff,
  onSlotCompletedWhileScreenOn,
} from './onCompleted';
import {
  getElapsedTimeSinceLastActiveInMillis,
  getElapsedTimeSinceLastActiveUntilMidnight,
  getElapsedTimeSinceSlotStartUntilMidnight,
  getElapsedTimeSlotInMillis,
} from './elapsed';
import { isDelegationStarted } from '../../delegation/delegation';
import { isProtectionEnabled } from '../../protectionStatus';
import { isSafeNetworkDelegated } from '../../safeNetworks/safeNetworksDelegation';
import { Mutex } from 'async-mutex';
import { onConsumedQuotaUpdated, onNewDay } from '../index';
import { DateTime } from '../../lib/dateTime/types/DateTime';
import { routineRuleChecker } from '../../checkers/routineRuleChecker';

/**
 * Gets the account timezone for all date/time calculations.
 * Falls back to system timezone if account is not yet initialized
 */
const getTimezone = () =>
  getRulesStore().get()?.timezone ?? getSystemTimezone();

// Max. frequency for syncing the Screen Time
const syncMaxFrequencyMs = 1000;
let lastSync = -1;

// The system is considered idle if detectionIntervalInSeconds seconds have elapsed since the last user input detected.
// The minimum allowed value is 15 seconds.
// The value it's not very relevant for this code, as we just differentiate with not being locked.
const detectionIntervalInSeconds = 15;

let screenTime: ScreenTime;

/**
 * Returns if it should sync the Screen Time.
 *
 * @remarks
 * There is a maximum of 1 sync per second to prevent any inconsistencies,
 * because checking the screen time too frequently could lead to performance issues, and it's really not necessary.
 *
 * Screen Time requires trusted time to be synchronized to prevent corrupted
 * elapsed time calculations from being persisted when the device clock is manipulated.
 */
export const shouldSyncScreenTime = async () =>
  trustedTime.isTrustedTimeSynced() &&
  isProtectionEnabled() &&
  isDelegationStarted() &&
  !isSafeNetworkDelegated() &&
  (await didMinimumTimePassSinceLastSync());

const didMinimumTimePassSinceLastSync = async () =>
  (await now()) >= lastSync + syncMaxFrequencyMs;

// Since we handle a queue of events on the reports, the execution needs to be synchronized.
const countingMutex = new Mutex();

/**
 * Will update the screen time usage state based on the current idle state of the device (locked or unlocked),
 * also taking into account if the device went into sleep mode,
 * and increment or report a period of usage when needed.
 */
export const syncScreenTime = async () => {
  lastSync = await now();

  return new Promise<void>(resolve => {
    getChromeIdle().queryState(detectionIntervalInSeconds, newState => {
      countingMutex
        .runExclusive(async () => {
          // Get last saved screen time; otherwise set the default one.
          screenTime = getScreenTimeStore().getScreenTime();
          const currentTime = await now();

          // Reset the daily counters on the first sync of a new day, regardless of
          // the screen state. lastSyncTimestamp advances on every sync, so a request
          // received while the device is locked must not consume the day boundary
          // without resetting (CHR-398).
          const didDayChange = resetScreenTimeIfDayChanged(currentTime);

          if (newState !== 'locked') {
            onScreenOn(currentTime, didDayChange);
          } else {
            onScreenOff(currentTime, didDayChange);
          }

          // Mark the last time the screen time was synced.
          screenTime.lastSyncTimestamp = currentTime;

          // Persist data after sync screen time usage
          await getScreenTimeStore().setScreenTime(screenTime);

          return onConsumedQuotaUpdated();
        })
        .finally(resolve);
    });
  });
};

// Flush the active slot before blocking so blocker time is not charged.
export const discardCurrentScreenTimeSlot = async () => {
  return countingMutex.runExclusive(async () => {
    screenTime = getScreenTimeStore().getScreenTime();
    const currentTime = await now();

    // A blocked device keeps discarding slots on every request. If it crosses
    // midnight while blocked, the day boundary must still reset the counter here;
    // otherwise lastSyncTimestamp advances into the new day without resetting and
    // the device stays blocked forever (CHR-398).
    const didDayChange = resetScreenTimeIfDayChanged(currentTime);

    if (!didDayChange) {
      const elapsedTimeSlotInMillis = getElapsedTimeSlotInMillis(
        currentTime,
        screenTime.slotStartTimestamp
      );
      const elapsedTimeSinceLastActiveInMillis =
        getElapsedTimeSinceLastActiveInMillis(
          currentTime,
          screenTime.lastActiveTimestamp
        );

      if (
        screenTime.slotStartTimestamp !== -1 &&
        toSeconds(elapsedTimeSlotInMillis) > 0
      ) {
        screenTime = onSlotCompleted(
          screenTime,
          elapsedTimeSlotInMillis,
          elapsedTimeSinceLastActiveInMillis
        );
      }
    }

    screenTime.slotStartTimestamp = -1;
    screenTime.elapsedTimeSlotInMillis = 0;
    screenTime.lastActiveTimestamp = -1;
    screenTime.lastSyncTimestamp = currentTime;

    await getScreenTimeStore().setScreenTime(screenTime);
  });
};

/**
 * Triggered after a screen time sync when the device is active (and not locked)
 */
const onScreenOn = (currentTime: number, didDayChange: boolean) => {
  // Last active timestamp will be -1 if it was never set
  const lastActiveTimestamp = screenTime.lastActiveTimestamp;
  screenTime.lastActiveTimestamp =
    lastActiveTimestamp !== -1 ? lastActiveTimestamp : currentTime;

  // Timestamp slot will be -1 if previously was screen off
  const slotStartTimestamp = screenTime.slotStartTimestamp;
  screenTime.slotStartTimestamp =
    slotStartTimestamp !== -1 ? slotStartTimestamp : currentTime;

  const timezone = getTimezone();

  const consumedProfileQuotaInMillis = getMsFromSeconds(
    getDeviceAndUserStore().get()?.consumedQuotaSeconds ?? 0
  );

  const shouldUpdate = shouldUpdateConsumedProfile(
    currentTime,
    consumedProfileQuotaInMillis,
    didDayChange
  );

  if (shouldUpdate) {
    const previousTotal = getTimeStringFromDuration(
      getDurationFromMs(screenTime.totalConsumedInMillis)
    );
    screenTime.totalConsumedInMillis = consumedProfileQuotaInMillis;
    const newTotal = getTimeStringFromDuration(
      getDurationFromMs(screenTime.totalConsumedInMillis)
    );
    console.log(
      `[ScreenTime] Updated quota from consumed profile: ${previousTotal} -> ${newTotal}`
    );
  }

  const elapsedTimeSlotInMillis = getElapsedTimeSlotInMillis(
    currentTime,
    screenTime.slotStartTimestamp
  );
  const elapsedTimeSinceLastActiveInMillis =
    getElapsedTimeSinceLastActiveInMillis(
      currentTime,
      screenTime.lastActiveTimestamp
    );

  const isSlotCompleted = isSlotCompletedWhileScreenOn(elapsedTimeSlotInMillis);
  const elapsedSlot = getTimeStringFromDuration(
    getDurationFromMs(elapsedTimeSlotInMillis)
  );
  const elapsedActive = getTimeStringFromDuration(
    getDurationFromMs(elapsedTimeSinceLastActiveInMillis)
  );
  // Reporting still flows through `addScreenTimeReport`; this flag only
  // gates the local quota counter so a non-counting routine does not
  // surface a BLOCK_BY_QUOTA in delegation.
  const countsTowardsDailyTimeQuota =
    routineRuleChecker.countsTowardsDailyTimeQuota();

  if (isSlotCompleted) {
    screenTime = onSlotCompletedWhileScreenOn(
      screenTime,
      elapsedTimeSlotInMillis,
      elapsedTimeSinceLastActiveInMillis,
      currentTime,
      countsTowardsDailyTimeQuota
    );
    const newTotal = getTimeStringFromDuration(
      getDurationFromMs(screenTime.totalConsumedInMillis)
    );
    console.log(
      `[ScreenTime] Slot completed (ON): elapsed=${elapsedSlot}, active=${elapsedActive} -> total=${newTotal}, currentTime=${currentTime}, rulesTimezone=${timezone}, countsTowardsDailyTimeQuota=${countsTowardsDailyTimeQuota}`
    );
  } else {
    // Period didn't finish yet.
    // Save elapsed time for period
    screenTime.elapsedTimeSlotInMillis = elapsedTimeSlotInMillis;

    // Increment total time, and keep same interval start.
    if (countsTowardsDailyTimeQuota) {
      screenTime.totalConsumedInMillis += elapsedTimeSinceLastActiveInMillis;
    }
  }

  // Chromebook is active, update the last active timestamp to now.
  screenTime.lastActiveTimestamp = currentTime;
};

/**
 * Triggered after a screen time sync when the device is locked
 */
const onScreenOff = (currentTime: number, didDayChange: boolean) => {
  // After a day change the counters were just reset and the device is locked, so
  // there is no in-progress slot to attribute. Invalidate it to avoid charging the
  // post-midnight gap as screen time (CHR-398).
  if (didDayChange) {
    screenTime.slotStartTimestamp = -1;
    screenTime.lastActiveTimestamp = -1;
    screenTime.elapsedTimeSlotInMillis = 0;
    return;
  }

  const elapsedTimeSlotInMillis = getElapsedTimeSlotInMillis(
    currentTime,
    screenTime.slotStartTimestamp
  );

  if (
    isSlotCompletedWhileScreenOff(
      elapsedTimeSlotInMillis,
      screenTime.slotStartTimestamp
    )
  ) {
    const elapsedTimeSlotInMillis = getElapsedTimeSlotInMillis(
      currentTime,
      screenTime.slotStartTimestamp
    );
    const elapsedTimeSinceLastActiveInMillis =
      getElapsedTimeSinceLastActiveInMillis(
        currentTime,
        screenTime.lastActiveTimestamp
      );

    const countsTowardsDailyTimeQuota =
      routineRuleChecker.countsTowardsDailyTimeQuota();

    screenTime = onSlotCompletedWhileScreenOff(
      screenTime,
      elapsedTimeSlotInMillis,
      elapsedTimeSinceLastActiveInMillis,
      countsTowardsDailyTimeQuota
    );

    const elapsedSlot = getTimeStringFromDuration(
      getDurationFromMs(elapsedTimeSlotInMillis)
    );
    const elapsedActive = getTimeStringFromDuration(
      getDurationFromMs(elapsedTimeSinceLastActiveInMillis)
    );
    const newTotal = getTimeStringFromDuration(
      getDurationFromMs(screenTime.totalConsumedInMillis)
    );
    console.log(
      `[ScreenTime] Slot completed (OFF): elapsed=${elapsedSlot}, active=${elapsedActive} -> total=${newTotal}, currentTime=${currentTime}, rulestimezone=${getTimezone()}, countsTowardsDailyTimeQuota=${countsTowardsDailyTimeQuota}`
    );
  }
};

/**
 * Check if should update consumed usage from profile.
 *
 * @remarks
 * If the day just changed, we don't want to overwrite the screen time with the backend consumed quota immediately
 * to avoid inconsistencies due to race conditions (CHR-343).
 *
 * Uses rules timezone to compare days, as the backend calculates consumedQuotaSeconds
 * based on the account timezone. This prevents incorrect overwrites when device and
 * account timezones differ.
 */
const shouldUpdateConsumedProfile = (
  currentTime: number,
  consumedProfileQuotaInMillis: number,
  didDayChange: boolean
) => {
  const timezone = getTimezone();
  const consumedQuotaTimestamp =
    getReportingStore().getTimestamp() ?? currentTime;
  const isQuotaFromSameDay = isSameDay(
    getDateTime(currentTime, timezone),
    getDateTime(consumedQuotaTimestamp, timezone)
  );
  const consumedQuotaIsGreaterThanScreenTime =
    consumedProfileQuotaInMillis > screenTime.totalConsumedInMillis;

  const shouldUpdate =
    isQuotaFromSameDay && consumedQuotaIsGreaterThanScreenTime && !didDayChange;
  if (shouldUpdate) {
    console.log(
      `[shouldUpdateConsumedProfile] true - currentTime=${currentTime}, timezone=${timezone}, consumedQuotaTimestamp=${consumedQuotaTimestamp}, isQuotaFromSameDay=${isQuotaFromSameDay}, consumedProfileQuotaInMillis=${consumedProfileQuotaInMillis}, screenTimeTotalConsumedInMillis=${screenTime.totalConsumedInMillis}, didDayChange=${didDayChange}`
    );
  }

  return shouldUpdate;
};

/**
 * Resets the screen time counters when the first sync of a new day is detected.
 *
 * @remarks
 * The day-change check must run regardless of whether the screen is on or off,
 * and on the quota-block discard path too: `lastSyncTimestamp` advances on every
 * sync, so if the first event of a new day arrives while the device is locked or
 * blocked, the boundary would be consumed without resetting and the daily counter
 * would accumulate across days (CHR-398).
 *
 * @returns Whether the day changed (and the counters were reset).
 */
const resetScreenTimeIfDayChanged = (currentTime: number) => {
  const timezone = getTimezone();
  const didDayChange = didDayChangeSinceLastSync(
    getDateTime(currentTime, timezone),
    timezone
  );
  if (didDayChange) {
    onDayChanged(currentTime, timezone);
  }
  return didDayChange;
};

const didDayChangeSinceLastSync = (currentDay: DateTime, timezone: string) => {
  // Fallback to use lastActiveTimestamp as the last sync timestamp
  // for devices that don't have lastSyncTimestamp yet (migration)
  // TODO: Remove this fallback once all devices have version 0.20 or higher
  if (screenTime.lastSyncTimestamp === undefined) {
    const lastActiveDay = getDateTime(screenTime.lastActiveTimestamp, timezone);
    return !isSameDay(currentDay, lastActiveDay);
  }

  // Skip for the first ever sync
  if (screenTime.lastSyncTimestamp === -1) {
    return false;
  }

  // Day changed if the current day is not the same as the last screen time sync day
  const lastSyncDay = getDateTime(screenTime.lastSyncTimestamp, timezone);
  return !isSameDay(currentDay, lastSyncDay);
};

const onDayChanged = (currentTime: number, timezone: string) => {
  const totalConsumedInMillis = getTimeStringFromDuration(
    getDurationFromMs(screenTime.totalConsumedInMillis)
  );

  console.info(`[ScreenTime] Day Changed!
    totalConsumedInMillis=${totalConsumedInMillis},
    lastSyncTimestamp=${screenTime.lastSyncTimestamp},
    lastActiveTimestamp=${screenTime.lastActiveTimestamp},
    currentTime=${currentTime},
    rulesTimezone=${timezone},
    systemTimezone=${getSystemTimezone()}`);

  // Start of the new day (in rules timezone).
  const startDayTimestampInMillis = getDateMs(
    getStartOfDay(getDateTime(currentTime, timezone))
  );

  // Only attribute time to the previous day when there is an in-progress slot that
  // actually started before midnight (i.e. it spans the day boundary). If the
  // device was off (slotStart === -1) or the slot already started after midnight,
  // there is no pre-midnight usage to attribute and the "until midnight" delta
  // would be invalid (negative or nonsensical).
  const slotSpansMidnight =
    screenTime.slotStartTimestamp !== -1 &&
    screenTime.slotStartTimestamp < startDayTimestampInMillis;

  if (slotSpansMidnight) {
    // Time until midnight (in rules timezone).
    const elapsedTimeSinceSlotStartUntilMidnight =
      getElapsedTimeSinceSlotStartUntilMidnight(
        currentTime,
        screenTime.slotStartTimestamp,
        timezone
      );
    const elapsedTimeSinceLastActiveUntilMidnight =
      getElapsedTimeSinceLastActiveUntilMidnight(
        currentTime,
        screenTime.lastActiveTimestamp,
        timezone
      );

    const gapUntilMidnight = getTimeStringFromDuration(
      getDurationFromMs(elapsedTimeSinceSlotStartUntilMidnight)
    );

    screenTime = onSlotCompleted(
      screenTime,
      elapsedTimeSinceSlotStartUntilMidnight,
      elapsedTimeSinceLastActiveUntilMidnight
    );

    // gapUntilMidnight is the raw distance from the slot start to midnight and can
    // be huge after a long period with no syncs; the amount actually reported is
    // capped by onSlotCompleted (see the "slot enqueued" log), so this value must
    // not be read as reported screen time.
    console.log(
      `[ScreenTime] Day reset: flushed previous-day slot (raw gap until midnight=${gapUntilMidnight}, reported amount is capped)`
    );
  }

  // Reset data because new day (using rules timezone for start of day)
  screenTime.totalConsumedInMillis = 0;
  screenTime.slotStartTimestamp = startDayTimestampInMillis;
  screenTime.elapsedTimeSlotInMillis = 0;
  screenTime.lastActiveTimestamp = startDayTimestampInMillis;

  console.log(
    `[ScreenTime] Day reset complete: new start: ${getISOfromDateTime(
      getDateTime(startDayTimestampInMillis, timezone)
    )}, currentTime=${currentTime}, rulesTimezone=${timezone}`
  );

  onNewDay().catch(error => {
    console.error('[ScreenTime] Failed to refresh tabs on new day:', error);
  });
};
